import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { money, monthLabel } from "../lib/format";

export function PageHeader({ eyebrow, title, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-up">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">{eyebrow}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
      </div>
      <div className="flex items-center gap-3 flex-wrap">{children}</div>
    </div>
  );
}

export function StatCard({ label, value, hint, accent, testId, delay = 0 }) {
  const color = accent === "emerald" ? "text-emerald-400" : accent === "amber" ? "text-amber-400" : "text-slate-50";
  const border = accent === "emerald" ? "border-emerald-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/40" : "border-slate-800 bg-[#131B2E]";
  const len = Math.max(String(value ?? "").length, 1);
  return (
    <div className={`fade-up min-w-0 overflow-hidden rounded-md border p-5 ${border}`} style={{ animationDelay: `${delay}ms`, containerType: "inline-size" }}>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 leading-snug">{label}</div>
      <div data-testid={testId} title={String(value ?? "")} className={`mt-3 font-mono font-bold tracking-tight whitespace-nowrap leading-tight ${color}`}
        style={{ fontSize: `clamp(0.75rem, calc(100cqw / ${(len * 0.64).toFixed(2)}), 1.875rem)` }}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400 break-words">{hint}</div>}
    </div>
  );
}

export function MonthSelect({ months, value, onChange }) {
  if (!months?.length) return null;
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid="month-selector" className="w-44 font-mono bg-[#131B2E]"><SelectValue /></SelectTrigger>
      <SelectContent>
        {months.map((m) => <SelectItem key={m} value={m} data-testid={`month-option-${m}`}>{monthLabel(m)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TrendChart({ data, keys }) {
  const rows = data.map((d) => ({ ...d, label: monthLabel(d.month) }));
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ left: 10, right: 10, top: 10 }}>
          <defs>
            {keys.map((k) => (
              <linearGradient key={k.key} id={`g-${k.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={k.color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={k.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="#1E293B" vertical={false} />
          <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
          <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)} />
          <Tooltip labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }} contentStyle={{ background: "#131B2E", border: "1px solid #1E293B", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => money(v)} />
          {keys.map((k) => <Area key={k.key} type="monotone" dataKey={k.key} name={k.label} stroke={k.color} strokeWidth={2} fill={`url(#g-${k.key})`} />)}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Panel({ title, action, children, className = "" }) {
  return (
    <section className={`fade-up rounded-md border border-slate-800 bg-[#131B2E] ${className}`}>
      {title && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h3 className="text-base font-medium">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Empty({ children }) {
  return <div className="p-10 text-center text-sm text-slate-500">{children}</div>;
}

const CARD_META = [
  { key: "visa", label: "Visa", color: "#3B82F6" },
  { key: "mastercard", label: "Mastercard", color: "#F59E0B" },
  { key: "discover", label: "Discover", color: "#F97316" },
  { key: "amex", label: "Amex", color: "#10B981" },
  { key: "debit", label: "Debit", color: "#94A3B8" },
];

export function CardBreakdown({ cards }) {
  const total = CARD_META.reduce((s, c) => s + (cards?.[c.key] || 0), 0);
  const data = CARD_META.map((c) => ({ ...c, value: cards?.[c.key] || 0 }));
  if (!total) return <div className="p-5 text-xs text-slate-500" data-testid="card-breakdown-empty">No card-type breakdown for this selection (Fiserv and Priority Payments reports only give total volume).</div>;
  return (
    <div className="p-5" data-testid="card-breakdown">
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={80} />
            <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }} contentStyle={{ background: "#131B2E", border: "1px solid #1E293B", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => money(v)} />
            <Bar dataKey="value" name="Volume" radius={[0, 3, 3, 0]}>{data.map((d) => <Cell key={d.key} fill={d.color} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 space-y-1.5">
        {data.map((d) => (
          <div key={d.key} className="flex items-center justify-between text-xs" data-testid={`card-${d.key}`}>
            <span className="flex items-center gap-2 text-slate-400"><span className="w-2 h-2 rounded-sm" style={{ background: d.color }} />{d.label}</span>
            <span className="font-mono text-slate-200">{money(d.value)} <span className="text-slate-500 ml-1">{total ? ((d.value / total) * 100).toFixed(1) : 0}%</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}
